import React from 'react'

export interface ButtonProps extends Omit<React.HTMLProps<HTMLButtonElement>, 'type' | 'size'> {
  type?:
    | 'primary'
    | 'secondary'
    | 'white'
    | 'neutral'
    | 'neutralBorder'
    | 'secondaryBorder'
    | 'neutralLink'
    | 'secondaryLink'
    | 'whiteShadow'
  size?: 'big' | 'medium' | 'small'
  isLoading?: boolean
  width?: string
}

export function Button({
  type = 'primary',
  size = 'medium',
  isLoading = false,
  className = '',
  children,
  width = 'auto',
  ...props
}: React.PropsWithChildren<ButtonProps>) {
  return (
    <button className={`button_ ${type} ${size} ${className} ${isLoading && 'is-loading'}`} {...props}>
      {children}
      <style jsx>{`
        .button_ {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-family: var(--font);
          font-weight: 600;
          border-radius: var(--round-8);
          padding: 4px 16px;
          height: 40px;
          width: ${width};
          transition: background-color var(--transition), border-color var(--transition), color var(--transition);

          color: var(--white-100);
          background-color: var(--primary-100);
          border: 2px solid;
          border-color: var(--primary-100);
          &:hover {
            background-color: var(--primary-100-darker);
            border-color: var(--primary-100-darker);
          }
          &:focus,
          &:active {
            background-color: var(--primary-100-dark);
            border-color: var(--primary-100-dark);
          }
          &:disabled {
            cursor: default;
            opacity: 0.5;
            pointer-events: none;
          }

          &.secondary {
            background-color: var(--secondary-100);
            border-color: var(--secondary-100);
            &:hover {
              background-color: var(--secondary-100-darker);
              border-color: var(--secondary-100-darker);
            }
            &:focus,
            &:active {
              background-color: var(--secondary-100-dark);
              border-color: var(--secondary-100-dark);
            }
          }

          &.white {
            background-color: var(--white-100);
            border-color: var(--white-100);
            color: var(--indigo-10);
            &:hover {
              background-color: var(--indigo-2);
              border-color: var(--indigo-2);
              color: var(--indigo-8);
            }
            &:focus,
            &:active {
              background-color: var(--indigo-1);
              border-color: var(--indigo-1);
              color: var(--indigo-8);
            }
          }

          &.whiteShadow {
            background-color: var(--white-100);
            border-color: var(--white-100);
            color: var(--indigo-10);
            box-shadow: var(--depth-8);
            &:hover {
              background-color: var(--indigo-2);
              border-color: var(--indigo-2);
              color: var(--indigo-8);
            }
            &:focus,
            &:active {
              background-color: var(--indigo-1);
              border-color: var(--indigo-1);
              color: var(--indigo-8);
            }
          }

          &.neutral {
            background-color: var(--indigo-2);
            border-color: var(--indigo-2);
            color: var(--indigo-10);
            &:hover {
              background-color: var(--indigo-4);
              border-color: var(--indigo-4);
              color: var(--indigo-8);
            }
            &:focus,
            &:active {
              background-color: var(--indigo-3);
              border-color: var(--indigo-3);
              color: var(--indigo-8);
            }
          }

          &.neutralBorder {
            background-color: var(--white-100);
            border-color: var(--indigo-4);
            color: var(--indigo-10);
            &:hover {
              background-color: var(--indigo-2);
              border-color: var(--indigo-6);
              color: var(--indigo-8);
            }
            &:focus,
            &:active {
              background-color: var(--indigo-1);
              border-color: var(--indigo-5);
              color: var(--indigo-8);
            }

            &.is-loading {
              color: transparent;
              &:after {
                border: 2px solid var(--indigo-5);
                border-right-color: transparent;
                border-top-color: transparent;
              }
            }
          }

          &.secondaryBorder {
            background-color: var(--white-100);
            border-color: var(--secondary-50);
            color: var(--secondary-100);
            &:hover {
              background-color: var(--indigo-2);
              border-color: var(--secondary-50-darker);
              color: var(--secondary-100);
            }
            &:focus,
            &:active {
              background-color: var(--indigo-1);
              border-color: var(--secondary-50-dark);
              color: var(--secondary-100);
            }
            &.is-loading {
              color: transparent;
              &:after {
                border: 2px solid var(--secondary-100);
                border-right-color: transparent;
                border-top-color: transparent;
              }
            }
          }

          &.neutralLink {
            background-color: var(--white-100);
            border-color: transparent;
            color: var(--indigo-10);
            &:hover {
              background-color: transparent;
              border-color: transparent;
              color: var(--indigo-6);
              text-decoration: underline;
            }
            &:focus,
            &:active {
              background-color: transparent;
              border-color: transparent;
              color: var(--indigo-8);
            }
          }

          &.secondaryLink {
            background-color: transparent;
            border-color: transparent;
            color: var(--secondary-100);
            &:hover {
              background-color: transparent;
              border-color: transparent;
              color: var(--secondary-100-darker);
              text-decoration: underline;
            }
            &:focus,
            &:active {
              background-color: transparent;
              border-color: transparent;
              color: var(--secondary-100-dark);
            }
          }

          &.big {
            height: 48px;
            font-family: var(--font-header);
            padding: 12px 24px;
            font-size: 14px;
            line-height: 24px;
          }

          &.small {
            height: 32px;
            border-radius: var(--round-4);
            padding: 4px 12px;
            font-size: 13px;
            line-height: 24px;
          }
        }
        .is-loading {
          color: transparent;
          pointer-events: none;
          position: relative;
          &:after {
            border-color: transparent transparent var(--white-100) var(--white-100);
            position: absolute;
            left: calc(50% - 0.5em);
            top: calc(50% - 0.5em);
            transform: translate(-50%, -50%);
            animation: spin 0.5s linear infinite;
            border: 2px solid var(--white-100);
            border-radius: 50%;
            border-right-color: transparent;
            border-top-color: transparent;
            content: '';
            display: block;
            height: 1em;
            width: 1em;
          }
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </button>
  )
}
